import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import useUsers from "containers/user-management/useHooks";
import { EventDTO } from "utils/helpers/models/common/event.dto";
import { EventsFilterDTO } from "utils/helpers/models/users/events-filter.dto";
import DataNotFound from "components/particles/table/data-not-found";
import { useSelector } from "react-redux";
import EventCard from "components/cards/EventCard";
import Pagination from "components/particles/table/pagination";
import { EVENT_STATUS } from "utils/helpers/enums/shared.enum";

interface Props {
  rsvpId: any;
}

const RsvpEvents: FC<Props> = ({ rsvpId }) => {
  console.log(rsvpId);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<EventsFilterDTO>(() => {
    const defaultFilters = new EventsFilterDTO();
    defaultFilters.timeFrame = EVENT_STATUS.UPCOMING;
    return defaultFilters;
  });
  const { getUserEventsById } = useUsers();
  const [data, setData] = useState<EventDTO[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<string>("upcoming");
  const { isLoading } = useSelector((state: any) => state.sharedReducer);

  const timeFilterTabs = [
    { title: "Upcoming Events", value: EVENT_STATUS.UPCOMING, id: "upcoming" },
    { title: "Past Events", value: EVENT_STATUS.PAST, id: "past" },
  ];

  const buildUpdatedFilters = (
    updates: Partial<EventsFilterDTO> = {},
    resetPage: boolean = false
  ) => {
    const updatedFilters = { ...filters, ...updates };
    if (resetPage) {
      updatedFilters.page = 0;
    }
    return updatedFilters;
  };

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    const updatedFilters = buildUpdatedFilters(
      { size: limit, page: page },
      false
    );
    setFilters(updatedFilters);
    getAllEvents(updatedFilters);
  };

  const getAllEvents = (filterParams: EventsFilterDTO, shouldClearData: boolean = true) => {
    // Clear data immediately when filtering/searching to show loading state
    if (shouldClearData) {
      setData([]);
    }

    const queryParams: any = { rsvpId };

    // Add pagination params
    queryParams.page = filterParams.page || 0;
    queryParams.size = filterParams.size || 8;

    // Build query params - only include non-empty values
    for (let key in filterParams) {
      if (
        key !== "rsvpId" &&
        filterParams[key]?.toString() &&
        filterParams[key] !== "" &&
        key !== "page" &&
        key !== "size"
      ) {
        queryParams[key] = filterParams[key];
      }
    }

    getUserEventsById(setData, queryParams, setFilters, setTotalElements);
  };

  // Load initial data on component mount
  useEffect(() => {
    getAllEvents(filters);
  }, []);

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target;
    const newFilters = { ...filters };
    
    if (name === "isSession") {
      newFilters[name] = value === "true" ? true : value === "false" ? false : "";
    } else {
      newFilters[name] = value;
    }
    
    setFilters(newFilters);
    if (name !== "searchTitle") {
      getAllEvents(newFilters);
    }
  };

  const handleTabChange = (value: string, tabId: string) => {
    // Reset data immediately to show loading state
    setData([]);

    const newFilters = { ...filters };
    
    // TimeFrame tab selected - remove status so it's not included in query
    newFilters.timeFrame = value;
    delete (newFilters as any).status;
    
    newFilters.isSession = "";

    // Reset pagination when changing tabs
    newFilters.page = 0;
    newFilters.size = 8;

    // Update selected tab
    setSelectedTab(tabId);

    setFilters(newFilters);
    getAllEvents(newFilters);
  };

  // Check if a tab is currently active
  const isTabActive = (tab: any) => {
    return selectedTab === tab.id;
  };

  console.log(data);

  const goToEventDetail = (id: number) => {
    navigate(`${siteRoutes.rsvpEventsDetail}?id=${id}`);
  };

  return (
    <div className="">
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="">
            <div className="flex space-x-4 ">
              {timeFilterTabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => handleTabChange(tab.value, tab.id)}
                  className={`text-xs font-semibold pb-1 flex items-center ${
                    isTabActive(tab)
                      ? "text-lightBlueColor border-b border-lightBlueColor"
                      : "text-mediumGrayText hover:text-lightBlueColor"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <select
            name="isSession"
            value={filters.isSession ?? ""}
            onChange={handleFilterChange}
            className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm h-[46px] w-full sm:w-auto min-w-[140px]"
          >
            <option value="">Event Type</option>
            <option value="true">Session</option>
            <option value="false">Workshop</option>
          </select>
        </div>
      </div>

      <div className="bg-white border-[0.3px] border-mediumGrayBorder rounded-[20px] p-6 my-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 my-6">
          {data.map((exp: EventDTO, index: number) => (
            <EventCard
              key={index + 1}
              event={exp}
              onClick={() => goToEventDetail(exp.id)}
            />
          ))}
        </div>
        <div className="">
          <DataNotFound show={!isLoading && !data.length} />
          {totalElements > 0 && (
            <Pagination
              count={totalElements}
              page={(filters.page || 0) + 1}
              limit={filters.size || 8}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RsvpEvents;
